/**
 * Follow-up chips — clickable next-action suggestions rendered below an
 * assistant turn. Max 3 per turn (orchestration spec §1).
 *
 * Picks the chip set from follow-up-registry based on the LAST tool the
 * assistant used in this turn.
 *
 * HANDOFF_SCORING_PROFILE_PROPAGATION D4: when the user has a scoring
 * profile set, lift the profile's lead chip to the first position when
 * it's available in the current tool's chip set. Order otherwise
 * preserved; chips are never removed.
 */
import { useAuthStore } from '../../../stores/authStore';
import { useChatTelemetry } from '../hooks/useChatTelemetry';
import { followUpRegistry, type FollowUpChip } from '../lib/follow-up-registry';

interface Props {
  toolNames: string[];
  onPick: (prompt: string) => void;
  /** Term sheet count from the most recent get_deal tool result. When ≤1,
   * the `compare-versions` chip is suppressed (nothing to compare against). */
  termSheetCount?: number;
}

const PROFILE_LEAD_CHIP: Record<string, string | null> = {
  rate_first:     'show-benchmarks',
  cost_conscious: 'negotiate',
  growth_focused: 'covenant-check',
  balanced:       null,
  not_set:        null,
};

function sortChipsByProfile(chips: FollowUpChip[], profile: string): FollowUpChip[] {
  const leadId = PROFILE_LEAD_CHIP[profile] ?? null;
  if (!leadId) {return chips;}
  const leadIndex = chips.findIndex((c) => c.id === leadId);
  if (leadIndex <= 0) {return chips;}
  const reordered = [...chips];
  const [lead] = reordered.splice(leadIndex, 1);
  reordered.unshift(lead);
  return reordered;
}

export function ChatFollowUpChips({ toolNames, onPick, termSheetCount }: Props) {
  const { logEvent } = useChatTelemetry();
  const profile = useAuthStore((s) => s.user)?.scoring_profile ?? 'not_set';
  if (toolNames.length === 0) {return null;}
  const lastTool = toolNames[toolNames.length - 1];
  let chips: FollowUpChip[] = followUpRegistry[lastTool] ?? [];
  if (typeof termSheetCount === 'number' && termSheetCount <= 1) {
    chips = chips.filter((c) => c.id !== 'compare-versions');
  }
  chips = sortChipsByProfile(chips, profile);
  chips = chips.slice(0, 3);
  if (chips.length === 0) {return null;}

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {chips.map((chip, position) => (
        <button
          key={chip.id}
          type="button"
          onClick={() => {
            logEvent('follow_up_clicked', {
              chip_id: chip.id,
              tool_name: lastTool,
              position,
            });
            onPick(chip.prompt);
          }}
          className="border border-border bg-surface-card px-3 py-1 text-body-sm text-txt-secondary transition-colors hover:border-orange hover:text-orange"
        >
          {chip.label}
        </button>
      ))}
    </div>
  );
}
